import { Show, createSignal, createEffect, type Component } from "solid-js";
import { useToolClient } from "@/shared/tool-client";
import { PanelBadge } from "@/shared/panel-primitives";

interface HostItem {
  hostId: string;
  hostname: string;
  displayName: string | null;
  persistentSessionCode: string | null;
  online: boolean;
}

interface MsraStatus {
  enabled?: boolean;
  allowFullControl?: boolean;
  termServiceRunning?: boolean;
  firewallEnabled?: boolean;
  denyTsConnections?: boolean;
}

interface MsraInvite {
  password: string;
  fileName: string;
  fileBase64: string;
}

const MsraInlinePanel: Component<{ host: HostItem | null; onClose: () => void }> = (props) => {
  const tools = useToolClient();
  const [status, setStatus] = createSignal<MsraStatus | null>(null);
  const [invite, setInvite] = createSignal<MsraInvite | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<string>("");

  createEffect(() => {
    if (props.host?.online) {
      void checkStatus();
    }
  });

  async function checkStatus() {
    setLoading(true);
    setError("");
    try {
      const res = await tools.call<MsraStatus>("msra_status", {});
      setStatus(res);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function enableMsra() {
    setLoading(true);
    setError("");
    try {
      const now = new Date().toISOString();
      await tools.call("msra_enable", { userConfirmedAt: now });
      await checkStatus();
    } catch (err) {
      setError(errMessage(err));
      setLoading(false);
    }
  }

  async function generateInvite() {
    setLoading(true);
    setError("");
    setInvite(null);
    try {
      const res = await tools.call<MsraInvite>("msra_invite", {});
      setInvite(res);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function downloadInvite() {
    const inv = invite();
    if (!inv) return;
    const blob = base64ToBlob(inv.fileBase64, "application/octet-stream");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = inv.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Accesseur réactif : NE PAS capturer `status()` dans un const (figé à null au
  // premier rendu, jamais re-évalué quand msra_status répond → panneau vide).
  const st = () => status();
  const isEnabled = () => st()?.enabled === true && st()?.termServiceRunning === true;

  return (
    <div
      class="msra-inline-panel"
      style={{ display: "flex", "flex-direction": "column", gap: "12px", padding: "16px" }}
    >
      <div style={{ display: "flex", "align-items": "center", "justify-content": "space-between" }}>
        <span style={{ "font-weight": 600 }}>Assistance à distance (MSRA)</span>
        <button class="btn btn--sm btn--ghost" onClick={props.onClose}>
          ← Retour
        </button>
      </div>

      <Show when={error()}>
        <PanelBadge intent="danger">{error()}</PanelBadge>
      </Show>

      <Show when={loading() && !invite()}>
        <div style={{ color: "var(--text-muted)" }}>Chargement…</div>
      </Show>

      <Show when={st() && !loading()}>
        <div style={{ display: "flex", "flex-direction": "column", gap: "8px" }}>
          <div style={{ display: "flex", "align-items": "center", gap: "8px" }}>
            <span>Remote Assistance :</span>
            {isEnabled() ? (
              <PanelBadge intent="ok">Activée</PanelBadge>
            ) : (
              <PanelBadge intent="warn">Désactivée</PanelBadge>
            )}
          </div>
          <div style={{ display: "flex", "align-items": "center", gap: "8px" }}>
            <span>Service TermService :</span>
            {st()?.termServiceRunning ? (
              <PanelBadge intent="ok">En cours</PanelBadge>
            ) : (
              <PanelBadge intent="danger">Arrêté</PanelBadge>
            )}
          </div>
          <div style={{ display: "flex", "align-items": "center", gap: "8px" }}>
            <span>Firewall :</span>
            {st()?.firewallEnabled ? (
              <PanelBadge intent="ok">OK</PanelBadge>
            ) : (
              <PanelBadge intent="warn">Règles manquantes</PanelBadge>
            )}
          </div>

          <Show when={!isEnabled()}>
            <button class="btn btn--sm btn--primary" onClick={enableMsra} disabled={loading()}>
              Activer MSRA
            </button>
          </Show>

          <Show when={isEnabled()}>
            <button class="btn btn--sm btn--primary" onClick={generateInvite} disabled={loading()}>
              Générer une invitation
            </button>
          </Show>
        </div>
      </Show>

      <Show when={invite()}>
        <div
          style={{
            display: "flex",
            "flex-direction": "column",
            gap: "10px",
            padding: "12px",
            "border-radius": "6px",
            border: "1px solid var(--border-default)",
            "background-color": "var(--bg-subtle)",
          }}
        >
          <div>
            <strong>Mot de passe :</strong>{" "}
            <code style={{ "font-size": "1.1em", "user-select": "all" }}>{invite()?.password}</code>
          </div>
          <div style={{ "font-size": "0.9em", color: "var(--text-muted)" }}>
            Téléchargez le fichier d'invitation et ouvrez-le avec <code>msra.exe</code> sur votre poste.
          </div>
          <div>
            <button class="btn btn--sm btn--primary" onClick={downloadInvite}>
              Télécharger l'invitation (.msrcIncident)
            </button>
          </div>
          <div>
            <button class="btn btn--sm btn--ghost" onClick={generateInvite} disabled={loading()}>
              Relancer
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64);
  const len = bin.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export default MsraInlinePanel;
